import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { Link } from 'gatsby';
import { Transition } from 'react-transition-group';
import { Typography } from '@curology/ui-components-web/react';

import APP_URLS from 'consts/appUrls';
import { CTA_FIND_YOUR_TREATMENT } from 'consts/copy';
import { CurologySiteAndShopNavCardFlag } from 'consts/featureFlagsAndMetrics';
import { PAGE_URLS } from 'consts/pageUrls';
import { MODULES } from 'consts/modules';
import { SlideDownModal } from 'components/global/modal';
import { useFlag } from 'utils/featureFlagsAndMetrics';
import { trackSignupCTAClick } from 'utils/metricsTracking';
import { ServicesModalDesktop } from './ModalDesktop/components';
import { NavLink } from './NavLink';
import { trackNavLoginClick } from '../utils/trackLoginClick';

import {
  PrimaryLinkContainer,
  ModalContainer,
  PageLinksContainer,
  AppLinksContainer,
  internalLinkStyles,
  StyledSignupLink,
} from './ModalMobile.style';
import { ProductLinks, Skincare101Links, NestableAccordion } from '.';
import { CategoryNavImageContainer } from './CategoryNav/CategoryNavProductsModalDesktop.style';

const MOBILE_MENU_BREAKPOINT = '(max-width: 1199px)';

let savedScrollY = 0;

const BODY_LOCK_STYLES = {
  position: 'fixed',
  left: '0',
  right: '0',
  width: '100%',
  overflowY: 'scroll',
} as const;

type BodyLockStyleKey = keyof typeof BODY_LOCK_STYLES | 'top';

const applyBodyLockStyles = (body: HTMLElement, scrollY: number) => {
  Object.assign(body.style, { ...BODY_LOCK_STYLES, top: `-${scrollY}px` });
};

const clearBodyLockStyles = (body: HTMLElement) => {
  const stylesToClear: BodyLockStyleKey[] = [
    ...Object.keys(BODY_LOCK_STYLES),
    'top',
  ] as BodyLockStyleKey[];
  stylesToClear.forEach((prop) => body.style.setProperty(prop, ''));
};

const toggleScrollLock = (shouldLock: boolean) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const { documentElement: html, body } = document;

  if (shouldLock) {
    const scrollY = savedScrollY || window.scrollY || 0;

    body.dataset.scrollY = String(scrollY);
    body.dataset.mobileMenuOpen = 'true';
    html.dataset.mobileMenuOpen = 'true';

    applyBodyLockStyles(body, scrollY);
    html.classList.add('no-scroll');
    body.classList.add('no-scroll');

    savedScrollY = 0;
  } else {
    const wasLocked = body.dataset.scrollY !== undefined;
    const scrollY = Number(body.dataset.scrollY || 0);

    delete body.dataset.mobileMenuOpen;
    delete html.dataset.mobileMenuOpen;
    delete body.dataset.scrollY;

    clearBodyLockStyles(body);
    html.classList.remove('no-scroll');
    body.classList.remove('no-scroll');

    if (wasLocked) {
      window.scrollTo(0, scrollY);
    }
  }
};

export const captureScrollPosition = () => {
  if (typeof window !== 'undefined') {
    savedScrollY = window.scrollY || 0;
  }
};

const subscribeToMediaQuery = (
  mediaQuery: MediaQueryList | null,
  callback: () => void,
) => {
  if (!mediaQuery) return () => undefined;

  mediaQuery.addEventListener('change', callback);
  return () => mediaQuery.removeEventListener('change', callback);
};

const useMobileMenuBodyLock = (isModalOpen: boolean) => {
  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_MENU_BREAKPOINT);

    const syncMobileMenuState = () => {
      const isMobileViewport = mediaQuery.matches;
      const shouldLockScroll = isModalOpen && isMobileViewport;
      toggleScrollLock(shouldLockScroll);
    };

    syncMobileMenuState();

    const unsubscribe = subscribeToMediaQuery(mediaQuery, syncMobileMenuState);

    return () => {
      unsubscribe();
      toggleScrollLock(false);
    };
  }, [isModalOpen]);
};

const useCloseOnEscape = (isModalOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isModalOpen || typeof document === 'undefined') return undefined;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isModalOpen, onClose]);
};

const useResetAccordions = (
  isModalOpen: boolean,
  resetProducts: () => void,
  resetSkincare: () => void,
) => {
  useEffect(() => {
    if (isModalOpen) return;
    resetProducts();
    resetSkincare();
  }, [isModalOpen, resetProducts, resetSkincare]);
};

const MenuSections = ({
  isHidingProductCtas,
  isProductsOpen,
  isSkincare101Open,
  setIsProductsOpen,
  setIsSkincare101Open,
  setIsModalOpen,
  curologySiteAndShopNavCard,
  ctaTextOverride,
}: {
  isHidingProductCtas: boolean;
  isProductsOpen: boolean;
  isSkincare101Open: boolean;
  setIsProductsOpen: (value: boolean) => void;
  setIsSkincare101Open: (value: boolean) => void;
  setIsModalOpen: (value: boolean) => void;
  curologySiteAndShopNavCard?: CurologySiteAndShopNavCardFlag;
  ctaTextOverride?: string;
}) => (
  <React.Fragment>
    <PageLinksContainer>
      {!isHidingProductCtas && (
        <PrimaryLinkContainer hasAccordion isAccordionOpen={isProductsOpen}>
          <NestableAccordion
            title={<Typography weight="bold">Shop</Typography>}
            isOpen={isProductsOpen}
            onClick={() => {
              setIsProductsOpen(!isProductsOpen);
              setIsSkincare101Open(false);
            }}
          >
            <ProductLinks
              isOpen={isProductsOpen}
              setIsModalOpen={setIsModalOpen}
              isMobile
            />
            {curologySiteAndShopNavCard && (
              <CategoryNavImageContainer>
                <Link to={curologySiteAndShopNavCard.linkUrl}>
                  <img
                    src={curologySiteAndShopNavCard.mobileImageUrl}
                    alt="Link to shop.curology.com"
                    width={curologySiteAndShopNavCard.mobileImageWidth}
                  />
                </Link>
              </CategoryNavImageContainer>
            )}
          </NestableAccordion>
        </PrimaryLinkContainer>
      )}
      <PrimaryLinkContainer onClick={() => setIsModalOpen(false)}>
        <Link to={PAGE_URLS.whyCurology} css={internalLinkStyles}>
          <Typography weight="bold">Why Curology</Typography>
        </Link>
      </PrimaryLinkContainer>
      <PrimaryLinkContainer onClick={() => setIsModalOpen(false)}>
        <Link to={PAGE_URLS.reviews} css={internalLinkStyles}>
          <Typography weight="bold">Reviews</Typography>
        </Link>
      </PrimaryLinkContainer>
      <PrimaryLinkContainer hasAccordion>
        <NestableAccordion
          title={<Typography weight="bold">Skincare 101</Typography>}
          isOpen={isSkincare101Open}
          onClick={() => {
            setIsSkincare101Open(!isSkincare101Open);
            setIsProductsOpen(false);
          }}
        >
          <Skincare101Links
            isOpen={isSkincare101Open}
            setIsModalOpen={setIsModalOpen}
            isMobile
          />
        </NestableAccordion>
      </PrimaryLinkContainer>
    </PageLinksContainer>
    <AppLinksContainer>
      <NavLink
        isExternal
        href={APP_URLS.login}
        trackClick={trackNavLoginClick}
        useSqueeze
      >
        Log In
      </NavLink>
      <StyledSignupLink
        isExternal
        // TODO [GE-3580]: Temporarily disables the "Find your treatment" modal in favor of just being directed to the skin SUF on click. Keeping the copy "Find your treatment" for the CTA though.
        href={APP_URLS.signUpSkin}
        // href={APP_URLS.findYourTreatment}
        trackClick={() => trackSignupCTAClick(MODULES.navigation)}
      >
        <Typography size="subhead">
          {ctaTextOverride || CTA_FIND_YOUR_TREATMENT}
        </Typography>
      </StyledSignupLink>
    </AppLinksContainer>
  </React.Fragment>
);

const MobileMenuOverlay = ({
  isModalOpen,
  bannerHeight,
  isServiceStepOpen,
  ...menuSectionProps
}: {
  isModalOpen: boolean;
  bannerHeight: number;
  isServiceStepOpen: boolean;
} & Parameters<typeof MenuSections>[0]) => (
  <Transition in={isModalOpen} timeout={250}>
    {(transitionState) => (
      <SlideDownModal
        isOpen={isModalOpen}
        distanceFromTop={`calc(var(--nav-height-mobile) + ${bannerHeight}px)`}
        isFullScreen
      >
        <ModalContainer transitionState={transitionState}>
          {!isServiceStepOpen && <MenuSections {...menuSectionProps} />}
          {isServiceStepOpen && <ServicesModalDesktop module={'nav'} />}
        </ModalContainer>
      </SlideDownModal>
    )}
  </Transition>
);

export const ModalMobile = ({
  isModalOpen,
  setIsModalOpen,
  bannerHeight,
  ctaTextOverride,
  isHidingProductCtas = false,
  isServiceStepOpen = false,
}: {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  bannerHeight: number;
  ctaTextOverride?: string;
  isHidingProductCtas?: boolean;
  isServiceStepOpen?: boolean;
}) => {
  const curologySiteAndShopNavCard = useFlag<
    CurologySiteAndShopNavCardFlag | undefined
  >('curology-site-and-shop-nav-card');

  const [isProductsOpen, setIsProductsOpen] = useState(true);
  const [isSkincare101Open, setIsSkincare101Open] = useState(false);

  const resetProducts = useCallback(() => setIsProductsOpen(true), []);
  const resetSkincare = useCallback(() => setIsSkincare101Open(false), []);

  useMobileMenuBodyLock(isModalOpen);
  useCloseOnEscape(isModalOpen, () => setIsModalOpen(false));
  useResetAccordions(isModalOpen, resetProducts, resetSkincare);

  return (
    <MobileMenuOverlay
      isModalOpen={isModalOpen}
      bannerHeight={bannerHeight}
      isServiceStepOpen={isServiceStepOpen}
      isHidingProductCtas={isHidingProductCtas}
      isProductsOpen={isProductsOpen}
      isSkincare101Open={isSkincare101Open}
      setIsProductsOpen={setIsProductsOpen}
      setIsSkincare101Open={setIsSkincare101Open}
      setIsModalOpen={setIsModalOpen}
      curologySiteAndShopNavCard={curologySiteAndShopNavCard}
      ctaTextOverride={ctaTextOverride}
    />
  );
};
